import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DialogAborted,
  openDialog,
  type Render,
} from '@/shared/modals/openDialog';

export type ModalWithPathname = {
  /**
   * Call this to open the modal.
   */
  openModal: () => void;
  /**
   * Call this to close the modal.
   */
  closeModal: () => void;
  /**
   * Whether the modal is open.
   */
  isModalOpen: boolean;
};

/**
 * A generic utility hook for managing modals that are controlled by pathname.
 *
 * This hook provides functionality to:
 * - Open a modal when the page loads and the pathname matches the modal open pathname
 * - Open a modal programmatically by calling the openModal function
 * - Update the browser pathname when the modal opens/closes
 * - Use AbortController to prevent the modal from being opened twice
 *
 * @param options Configuration options for the modal
 * @param options.onClose Callback to be called when the modal is closed
 * @param options.modalOpenPathname The pathname that indicates the modal should be open
 * @param options.modalClosedPathname The pathname to navigate to when the modal is closed
 * @param options.getModalElement Function that returns a promise resolving to the modal element
 */
export function useModalWithPathname({
  onClose,
  modalOpenPathname,
  modalClosedPathname,
  getModalElement,
}: {
  /**
   * The callback to be called when the modal is closed.
   */
  onClose?: () => void;
  /**
   * The pathname that indicates the modal should be open.
   */
  modalOpenPathname: string;
  /**
   * The pathname to navigate to when the modal is closed.
   */
  modalClosedPathname: string;
  /**
   * Function that returns a promise resolving to the modal element.
   * The function receives the same parameters as the openDialog render function.
   */
  getModalElement: Render<void>;
}): ModalWithPathname {
  // Use refs to capture the latest values of potentially unstable functions
  // This prevents the effect from re-running when these functions change
  const getModalElementRef = useRef(getModalElement);
  const onCloseRef = useRef(onClose);
  const openPathnameRef = useRef(modalOpenPathname);
  openPathnameRef.current = modalOpenPathname;
  const closedPathnameRef = useRef(modalClosedPathname);
  closedPathnameRef.current = modalClosedPathname;

  // Keep refs up to date with the latest values
  getModalElementRef.current = getModalElement;
  onCloseRef.current = onClose;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const isModalOpenRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const modalOpener = useCallback(() => {
    if (isModalOpenRef.current) {
      return;
    }
    setIsModalOpen(true);
    isModalOpenRef.current = true;

    history.replaceState(null, '', openPathnameRef.current);
    abortControllerRef.current = new AbortController();
    openDialog(getModalElementRef.current, {
      abortSignal: abortControllerRef.current.signal,
    })
      .then(() => {
        onCloseRef.current?.();
      })
      .catch((error) => {
        if (error instanceof DialogAborted) {
          return;
        }
        throw error;
      })
      .finally(() => {
        setIsModalOpen(false);
        isModalOpenRef.current = false;
        history.replaceState(null, '', closedPathnameRef.current);
      });
  }, []);

  const closeModal = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const openModal = useCallback(() => {
    modalOpener();
  }, []);

  useEffect(() => {
    if (location.pathname === openPathnameRef.current) {
      modalOpener();
    }
  }, []);

  return {
    openModal,
    closeModal,
    isModalOpen,
  };
}
